import { Component, OnInit, ChangeDetectionStrategy, inject, ChangeDetectorRef } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { DrawerModule } from 'primeng/drawer';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputNumberModule } from 'primeng/inputnumber';
import { DividerModule } from 'primeng/divider';
import { MultiSelectModule } from 'primeng/multiselect';
import { AdminService } from '@generated/services/admin.service';
import { NotificationService } from '@core/services/notification.service';
import { PlanDto, UpdatePlanCommand, UserListItem } from '@generated/models';
import { environment } from '@env/environment';

@Component({
  selector: 'app-plans',
  standalone: true,
  imports: [FormsModule, ButtonModule, TableModule, DrawerModule, InputTextModule, ProgressSpinnerModule, ToggleSwitchModule, InputNumberModule, DividerModule, MultiSelectModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './plans.html',
  styleUrl: './plans.css'
})
export class PlansComponent implements OnInit {
  private adminService = inject(AdminService);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);
  plans: PlanDto[] = [];
  users: UserListItem[] = [];
  isLoading = false;
  isSaving = false;
  searchQuery = '';
  get filteredPlans(): PlanDto[] {
    if (!this.searchQuery.trim()) return this.plans;
    const query = this.searchQuery.toLowerCase();
    return this.plans.filter(plan => plan.code?.toLowerCase().includes(query) || plan.name?.toLowerCase().includes(query));
  }
  get userOptions() {
    return this.users.map(user => ({ label: user.email || user.id, value: user.id }));
  }
  showDrawer = false;
  editingPlan: PlanDto = {};
  ngOnInit(): void { this.loadData(); }
  private loadData(): void {
    this.isLoading = true;
    this.cdr.markForCheck();
    this.adminService.apiAdminUsersGet().subscribe({
      next: (users) => {
        this.users = users;
        this.loadPlans();
      },
      error: (error) => {
        console.error('Failed to load users:', error);
        this.notificationService.showError('Failed to load users');
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }
  private loadPlans(): void {
    this.adminService.apiAdminBillingPlansGet().subscribe({
      next: (result) => { this.plans = result.plans || []; this.isLoading = false; this.cdr.markForCheck(); },
      error: (error) => { console.error('Failed to load plans:', error); this.notificationService.showError('Failed to load plans'); this.isLoading = false; this.cdr.markForCheck(); }
    });
  }
  onAddPlanClick(): void { window.open(`${environment.billing.url.replace(/\/$/, '')}/create/plans`, '_blank'); }
  onEditClick(plan: PlanDto): void {
    this.editingPlan = { ...plan };
    if (!this.editingPlan.progressive_billing_thresholds) this.editingPlan.progressive_billing_thresholds = [];
    if (!this.editingPlan.user_ids) this.editingPlan.user_ids = [];
    this.showDrawer = true;
    this.cdr.markForCheck();
  }
  onCancel(): void { this.showDrawer = false; this.editingPlan = {}; this.cdr.markForCheck(); }
  onSave(): void {
    if (!this.editingPlan.code) { this.notificationService.showError('Plan code is missing'); return; }
    this.isSaving = true;
    this.cdr.markForCheck();
    const request: UpdatePlanCommand = {
      code: this.editingPlan.code,
      visible: this.editingPlan.visible,
      progressive_billing_enabled: this.editingPlan.progressive_billing_enabled,
      progressive_billing_thresholds: this.editingPlan.progressive_billing_thresholds,
      progressive_billing_step_amount: this.editingPlan.progressive_billing_step_amount,
      termination_amount: this.editingPlan.termination_amount,
      graduated_pricing_billing_enabled: this.editingPlan.graduated_pricing_billing_enabled,
      visible_to_specified_users: this.editingPlan.visible_to_specified_users,
      user_ids: this.editingPlan.user_ids,
      stt_coeff: this.editingPlan.stt_coeff,
      llm_input_token_price_coeff: this.editingPlan.llm_input_token_price_coeff,
      llm_output_token_price_coeff: this.editingPlan.llm_output_token_price_coeff,
      tts_coeff: this.editingPlan.tts_coeff
    };
    this.adminService.apiAdminBillingPlansCodePut({ code: this.editingPlan.code, body: request }).subscribe({
      next: (result) => { this.notificationService.showSuccess('Plan updated successfully'); this.loadPlans(); this.showDrawer = false; this.editingPlan = {}; this.isSaving = false; this.cdr.markForCheck(); },
      error: (error) => { console.error('Failed to update plan:', error); this.notificationService.showError('Failed to update plan'); this.isSaving = false; this.cdr.markForCheck(); }
    });
  }
  addThreshold(): void { if (!this.editingPlan.progressive_billing_thresholds) this.editingPlan.progressive_billing_thresholds = []; this.editingPlan.progressive_billing_thresholds.push(0); this.cdr.markForCheck(); }
  removeThreshold(index: number): void { this.editingPlan.progressive_billing_thresholds?.splice(index, 1); this.cdr.markForCheck(); }
  formatDate(dateString: string | undefined | null): string { if (!dateString) return '-'; return new Date(dateString).toLocaleDateString(); }
  formatAmount(amountCents: number | undefined, currency: string | null | undefined): string { if (amountCents === undefined || amountCents === null) return '0.00'; const amount = amountCents / 100; return amount.toFixed(2) + ' ' + (currency || 'USD'); }
  getLagoBillingUrl(): string { return `${environment.billing.url.replace(/\/$/, '')}/plan/${this.editingPlan.lago_id}/overview`; }
  hasGraduatedRanges(): boolean {
    if (!this.editingPlan.charges || this.editingPlan.charges.length === 0) return false;
    return this.editingPlan.charges.some(charge => charge.properties?.graduated_ranges && charge.properties.graduated_ranges.length > 0);
  }
  canEnableGraduatedPricingBilling(): boolean {
    if (!this.hasGraduatedRanges()) return false;
    return this.editingPlan.charges!.some(charge => {
      if (!charge.properties?.graduated_ranges) return false;
      return charge.properties.graduated_ranges.some(range => {
        const flatAmount = parseFloat(range.flat_amount || '0');
        const perUnitAmount = parseFloat(range.per_unit_amount || '0');
        return flatAmount > 0 || perUnitAmount > 0;
      });
    });
  }
  canEnableProgressiveBilling(): boolean {
    return this.canEnableGraduatedPricingBilling();
  }
}
